import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { lightTheme } from '../../../styles/themes/light';
import { darkTheme } from '../../../styles/themes/dark';
import type { Theme, ThemeName } from '../../../styles/themes/types';

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: lightTheme,
  isDark: false,
  toggleTheme: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  themeName: ThemeName;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  themeName,
}) => {
  const [isDark, setIsDark] = useState(() => {
    localStorage.setItem('theme', themeName);
    return themeName === 'dark';
  });

  const theme = isDark ? darkTheme : lightTheme;

  const toggleTheme = useCallback(() => {
    setIsDark(prev => {
      const newTheme = !prev ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      return !prev;
    });
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    // Set theme class
    root.classList.remove('dark-theme', 'light-theme');
    root.classList.add(isDark ? 'dark-theme' : 'light-theme');

    // Set core colors
    root.style.setProperty('--color-background-main', theme.colors.bg.primary);
    root.style.setProperty('--color-background-surface', theme.colors.bg.secondary);
    root.style.setProperty('--color-background-elevated', theme.colors.bg.tertiary);
    root.style.setProperty('--color-text-primary', theme.colors.fg.primary);
    root.style.setProperty('--color-text-secondary', theme.colors.fg.secondary);
    root.style.setProperty('--color-text-disabled', theme.colors.fg.tertiary);
    root.style.setProperty('--color-primary', theme.colors.accent.primary);
    root.style.setProperty('--color-secondary', theme.colors.accent.secondary);
    root.style.setProperty('--color-border', theme.colors.border.light);

    // Set NewNG brand colors
    root.style.setProperty('--newng-red', theme.colors.newng.red);
    root.style.setProperty('--newng-blue', theme.colors.newng.blue);
    root.style.setProperty('--newng-white', theme.colors.newng.white);
    root.style.setProperty('--newng-black', theme.colors.newng.black);

    // Set button styles
    root.style.setProperty('--button-primary-background', theme.buttons.primary.background);
    root.style.setProperty('--button-primary-text', theme.buttons.primary.text);
    root.style.setProperty('--button-primary-hover', theme.buttons.primary.hover);
    root.style.setProperty('--button-primary-active', theme.buttons.primary.active);

    root.style.setProperty('--button-secondary-background', theme.buttons.secondary.background);
    root.style.setProperty('--button-secondary-text', theme.buttons.secondary.text);
    root.style.setProperty('--button-secondary-hover', theme.buttons.secondary.hover);
    root.style.setProperty('--button-secondary-active', theme.buttons.secondary.active);

    root.style.setProperty('--button-error-background', theme.buttons.error.background);
    root.style.setProperty('--button-error-text', theme.buttons.error.text);
    root.style.setProperty('--button-error-hover', theme.buttons.error.hover);
    root.style.setProperty('--button-error-active', theme.buttons.error.active);

    root.style.setProperty('--button-disabled-opacity', theme.buttons.disabled.opacity.toString());
    root.style.setProperty('--button-border-radius', theme.buttons.borderRadius);
    root.style.setProperty('--button-transition', theme.buttons.transition);

    // Set effects
    root.style.setProperty('--effects-gradient-primary', theme.colors.gradients.primary);
    root.style.setProperty('--effects-gradient-dark', theme.colors.gradients.surface);
    root.style.setProperty('--effects-gradient-accent', theme.colors.gradients.accent);

    // Set glass panel styles (for frosted glass UI)
    if (isDark) {
      root.style.setProperty('--panel-glass-bg', 'rgba(255, 255, 255, 0.06)');
      root.style.setProperty('--panel-glass-hover', 'rgba(255, 255, 255, 0.12)');
      root.style.setProperty('--panel-glass-border', 'rgba(255, 255, 255, 0.12)');
      root.style.setProperty('--panel-glass-border-hover', 'rgba(255, 255, 255, 0.2)');
      root.style.setProperty('--panel-glass-shadow', '0 8px 32px rgba(0, 0, 0, 0.4)');
      root.style.setProperty('--panel-inner-bg', 'rgba(255, 255, 255, 0.04)');
      root.style.setProperty('--panel-inner-border', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--input-bg', 'rgba(0, 0, 0, 0.3)');
      root.style.setProperty('--input-border', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--input-border-focus', 'rgba(255, 255, 255, 0.25)');
      // Select dropdown options (native elements)
      root.style.setProperty('--select-option-bg', '#1e1e2e');
      root.style.setProperty('--select-option-text', '#e0e0e0');
      root.style.setProperty('--select-option-hover-bg', '#3b3b5c');
    } else {
      root.style.setProperty('--panel-glass-bg', 'rgba(255, 255, 255, 0.7)');
      root.style.setProperty('--panel-glass-hover', 'rgba(255, 255, 255, 0.85)');
      root.style.setProperty('--panel-glass-border', 'rgba(0, 0, 0, 0.08)');
      root.style.setProperty('--panel-glass-border-hover', 'rgba(0, 0, 0, 0.15)');
      root.style.setProperty('--panel-glass-shadow', '0 4px 24px rgba(0, 0, 0, 0.06)');
      root.style.setProperty('--panel-inner-bg', 'rgba(255, 255, 255, 0.5)');
      root.style.setProperty('--panel-inner-border', 'rgba(0, 0, 0, 0.06)');
      root.style.setProperty('--input-bg', 'rgba(255, 255, 255, 0.8)');
      root.style.setProperty('--input-border', 'rgba(0, 0, 0, 0.1)');
      root.style.setProperty('--input-border-focus', 'rgba(0, 0, 0, 0.2)');
      // Select dropdown options (native elements)
      root.style.setProperty('--select-option-bg', '#ffffff');
      root.style.setProperty('--select-option-text', '#1a1a1a');
      root.style.setProperty('--select-option-hover-bg', '#f0f0f0');
    }

    // Set body styles consistently
    document.body.style.backgroundColor = theme.colors.bg.primary;
    document.body.style.color = theme.colors.fg.primary;

  }, [theme, isDark]);

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;